import React, { useState } from "react";
import { isTeacherMember } from "../utils/roleUtils";
import { DEFAULT_THEME } from "../constants/theme";
import {
  Users,
  GraduationCap,
  Calendar,
  CheckSquare,
  MessageCircle,
  DollarSign,
  Sparkles,
  X,
  LogOut,
  Database,
  House,
  BarChart3,
  ChevronLeft,
  ChevronRight,
  Settings, // [NEW] 톱니바퀴 아이콘 추가
} from "lucide-react";

export default function Sidebar({
  activeMenu,
  setActiveMenu,
  isMobileMenuOpen,
  setIsMobileMenuOpen,
  deptName,
  user,
  userProfile,
  handleLogout,
  onOpenProfile,
  selectedDepartment,
  theme,
  isMobile = false // [NEW] Accept isMobile prop
}) {
  const [isCollapsed, setIsCollapsed] = useState(false);

  // Fallback theme if undefined
  const activeTheme = theme || DEFAULT_THEME;

  const handleMenuClick = (menu) => {
    setActiveMenu(menu);
    setIsMobileMenuOpen(false);
  };

  // [NEW] Role-based access control
  const isTeacher = isTeacherMember(userProfile);

  const getDisplayName = () => {
    if (userProfile?.name) return userProfile.name;
    if (user?.isAnonymous) return "게스트";
    return "관리자";
  };

  const getInitial = () => {
    return getDisplayName().charAt(0).toUpperCase();
  };

  return (
    <>


      <aside
        className={`
        flex flex-col
        ${isMobile ? "bg-white" : "bg-white/60 backdrop-blur-xl border-r border-white/40"}
        ${isMobile
            ? "w-full h-full"
            : `fixed inset-y-0 left-0 z-50 transform transition-transform duration-300 ${isMobileMenuOpen ? "translate-x-0" : "-translate-x-full"} md:translate-x-0 md:static md:inset-auto ${isCollapsed ? "w-20" : "w-64"}`
          }
      `}
      >
        {/* 사이드바 토글 버튼 (데스크탑) */}
        {/* 헤더 */}
        <div className={`${isMobile ? "pt-[calc(env(safe-area-inset-top)+40px)] pb-6" : "p-6"} ${isCollapsed ? "px-4 items-center flex flex-col gap-4" : "px-6 flex items-center justify-between"}`}>
          <div className={`flex items-center ${isCollapsed ? "justify-center" : "gap-3"}`}>
            {/* 로고 */}
            <h1
              onClick={() => handleMenuClick("dashboard")}
              className={`font-black ${activeTheme.text} tracking-tight leading-none cursor-pointer hover:opacity-70 transition-opacity font-do-hyeon ${isCollapsed ? "text-sm text-center" : "text-xl"}`}
            >
              {isCollapsed ? "TS" : "The STAFF"}
            </h1>
          </div>

          {/* 사이드바 토글 버튼 (내부) */}
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className={`hidden md:flex items-center justify-center w-8 h-8 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors ${isCollapsed ? "" : ""}`}
          >
            {isCollapsed ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
          </button>

          <button
            onClick={() => setIsMobileMenuOpen(false)}
            className="md:hidden text-gray-400 hover:text-gray-600"
          >
            <X size={24} />
          </button>
        </div>

        {!isCollapsed && (
          <div className="px-6 pb-4 animate-fade-in">
            <p className="text-sm font-bold text-gray-700 truncate">
              {userProfile?.churchName || "우리교회"} {
                // [FIX] When only one department, show that department name instead of "전체 부서"
                (() => {
                  const hasExtraDepts = userProfile?.myDepartments && userProfile.myDepartments.filter(d => d !== deptName).length > 0;
                  if (!hasExtraDepts) {
                    // Only one department - show it directly
                    return deptName || selectedDepartment;
                  }
                  // Multiple departments - show "전체 부서" if All selected
                  return selectedDepartment === 'All' ? '전체 부서' : (selectedDepartment || deptName);
                })()
              }
            </p>
            <p className="text-[10px] text-gray-500 font-medium tracking-wider mt-1 ">
              Ministry Admin Platform
            </p>
          </div>
        )}

        {/* 메뉴 */}
        <nav className="flex-1 px-3 space-y-1 overflow-y-auto no-scrollbar pb-6">
          <SidebarItem
            label="홈 Home"
            active={activeMenu === "dashboard"}
            onClick={() => handleMenuClick("dashboard")}
            icon={<House size={18} />}
            isCollapsed={isCollapsed}
            theme={activeTheme}
          />

          {!isCollapsed && (
            <div className="pt-6 pb-2 px-4 text-[10px] font-extrabold text-gray-400 uppercase tracking-widest">
              Members
            </div>
          )}
          {isCollapsed && <div className="h-4"></div>}

          {isMobile ? (
            <SidebarItem
              label="명단 List"
              active={activeMenu === "students" || activeMenu === "teachers"}
              onClick={() => handleMenuClick("students")}
              icon={<Users size={18} />}
              isCollapsed={isCollapsed}
              theme={activeTheme}
            />
          ) : (
            <>
              <SidebarItem
                label="학생 명단 Students"
                active={activeMenu === "students"}
                onClick={() => handleMenuClick("students")}
                icon={<Users size={18} />}
                isCollapsed={isCollapsed}
                theme={activeTheme}
              />
              <SidebarItem
                label="교사 명단 Teachers"
                active={activeMenu === "teachers"}
                onClick={() => handleMenuClick("teachers")}
                icon={<GraduationCap size={18} />}
                isCollapsed={isCollapsed}
                theme={activeTheme}
              />
            </>
          )}
          <SidebarItem
            label="출석부 Attendence"
            active={activeMenu === "attendance"}
            onClick={() => handleMenuClick("attendance")}
            icon={<Calendar size={18} />}
            isCollapsed={isCollapsed}
            theme={activeTheme}
          />
          {/* [MOD] 모바일에서는 숨김 - 명단 상세화면에서 심방 작성 가능 */}
          {!isMobile && (
            <SidebarItem
              label="심방 일지 Counseling"
              active={activeMenu === "counseling"}
              onClick={() => handleMenuClick("counseling")}
              icon={<MessageCircle size={18} />}
              isCollapsed={isCollapsed}
              theme={activeTheme}
            />
          )}

          {!isCollapsed && (
            <div className="pt-6 pb-2 px-4 text-[10px] font-extrabold text-gray-400 uppercase tracking-widest">
              Ministry
            </div>
          )}
          {isCollapsed && <div className="h-4"></div>}

          <SidebarItem
            label="사역 캘린더 Calendar"
            active={activeMenu === "schedule"}
            onClick={() => handleMenuClick("schedule")}
            icon={<CheckSquare size={18} />}
            isCollapsed={isCollapsed}
            theme={activeTheme}
          />
          <SidebarItem
            label="재정 관리 Finance"
            active={activeMenu === "finance"}
            onClick={() => handleMenuClick("finance")}
            icon={<DollarSign size={18} />}
            isCollapsed={isCollapsed}
            theme={activeTheme}
          />
          <SidebarItem
            label="사역 통계 Statistics"
            active={activeMenu === "statistics"}
            onClick={() => handleMenuClick("statistics")}
            icon={<BarChart3 size={18} />}
            isCollapsed={isCollapsed}
            theme={activeTheme}
          />

          {!isCollapsed && (
            <div className="pt-6 pb-2 px-4 text-[10px] font-extrabold text-gray-400 uppercase tracking-widest">
              Tools
            </div>
          )}
          {isCollapsed && <div className="h-4"></div>}

          {/* [REMOVED] 아론 AI 탭 삭제 - 사역 통계에 AI 기능 통합 */}
          {!isTeacher && (
            <SidebarItem
              label="데이터 센터 Data Center"
              active={activeMenu === "migration"}
              onClick={() => handleMenuClick("migration")}
              icon={<Database size={18} />}
              isCollapsed={isCollapsed}
              theme={activeTheme}
            />
          )}

          {/* [NEW] Mobile-only Account Menu Items */}
          {isMobile && (
            <>
              <div className="pt-6 pb-2 px-4 text-[10px] font-extrabold text-gray-400 uppercase tracking-widest">
                Account
              </div>
              <SidebarItem
                label="내 정보 수정 My Info"
                active={false}
                onClick={() => { onOpenProfile(); setIsMobileMenuOpen(false); }}
                icon={<div className="w-[18px] h-[18px] rounded-full bg-gray-200 flex items-center justify-center text-[10px] font-bold text-gray-600">{getInitial()}</div>}
                isCollapsed={isCollapsed}
                theme={activeTheme}
              />
              <SidebarItem
                label="로그아웃 Sign Out"
                active={false}
                onClick={handleLogout}
                icon={<LogOut size={18} className="text-rose-400" />}
                isCollapsed={isCollapsed}
                theme={activeTheme}
              />
              <div className="h-20"></div> {/* Spacer for bottom safe area */}
            </>
          )}
        </nav>

        {/* 프로필 (하단) */}
        <div className="p-4 border-t border-white/40">
          <div className={`flex items-center ${isCollapsed ? "justify-center flex-col gap-4" : "justify-between"}`}>
            <div
              className={`flex items-center gap-3 cursor-pointer group ${isCollapsed ? "justify-center" : ""}`}
              onClick={onOpenProfile}
            >
              <div
                className={`w-9 h-9 rounded-full flex items-center justify-center font-bold text-sm text-white shadow-sm transition-transform group-hover:scale-105 ${user?.isAnonymous ? "bg-gray-400" : activeTheme.bg
                  }`}
              >
                {getInitial()}
              </div>
              {!isCollapsed && (
                <div className="flex-1 min-w-0 flex items-center gap-2">
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-bold text-gray-800 group-hover:${activeTheme.text} transition-colors truncate`}>
                      {getDisplayName()}
                    </p>
                    <p className="text-[10px] text-gray-500 truncate">
                      {user?.isAnonymous ? "체험판" : userProfile?.role || "사역자"}
                    </p>
                  </div>
                  {/* [NEW] 톱니바퀴 아이콘 - 내 정보 수정 가능 표시 */}
                  <Settings
                    size={14}
                    className="text-gray-400 group-hover:text-gray-600 group-hover:rotate-90 transition-all duration-300 flex-shrink-0"
                  />
                </div>
              )}
            </div>
            <button
              onClick={handleLogout}
              className={`text-gray-400 hover:text-red-500 hover:bg-red-50/50 p-2 rounded-lg transition-colors ${isCollapsed ? "" : ""}`}
              title="로그아웃"
            >
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </aside>
    </>
  );
}

function SidebarItem({ label, active, onClick, icon, isCollapsed, theme }) {
  return (
    <button
      onClick={onClick}
      className={`
        w-full flex items-center ${isCollapsed ? "justify-center px-2" : "gap-3 px-4"} py-3 rounded-lg transition-all duration-200 ease-out text-sm font-medium group relative overflow-hidden
        ${active
          ? `${theme.bg} text-white shadow-md ${theme.shadow}` // 활성
          : `text-gray-600 hover:${theme.lightBg} hover:${theme.text}` // 비활성 호버
        }
      `}
      title={isCollapsed ? label : ""}
    >
      <span
        className={`transition-all duration-200 ${active ? "scale-110" : "group-hover:scale-110"
          }`}
      >
        {icon}
      </span>
      {!isCollapsed && <span className="transition-colors duration-200">{label}</span>}
    </button>
  );
}
